import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Edit } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';
import { format } from 'date-fns';
import { Lead as LeadType } from '@/types/leads/Table';

interface Lead {
  uuid: string;
  lead_id: number;
  name: string;
  email: string;
  phone: string;
  message?: string;
  source?: number;
  status?: number;
  created_date: string;
  last_updated_date?: string;
  duplicate_count?: number;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

interface Remark {
  remark_id: number;
  uuid: string;
  remark: string;
  user?: {
    userName: string;
  };
  date_remark: string;
}

interface DuplicateReviewSliderProps {
  isOpen: boolean;
  onClose: () => void;
  phone: string;
  onReviewComplete?: () => void;
  onEditLead?: (lead: LeadType) => void;
}

const DuplicateReviewSlider: React.FC<DuplicateReviewSliderProps> = ({
  isOpen,
  onClose,
  phone,
  onReviewComplete,
  onEditLead
}) => {
  const [primary, setPrimary] = useState<Lead | null>(null);
  const [duplicates, setDuplicates] = useState<Lead[]>([]);
  const [primaryRemarks, setPrimaryRemarks] = useState<Remark[]>([]);
  const [loading, setLoading] = useState(false);
  const [reviewRemark, setReviewRemark] = useState('');
  const [selectedDuplicates, setSelectedDuplicates] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen && phone) {
      fetchLeadsByPhone();
    }
  }, [isOpen, phone]);

  const fetchLeadsByPhone = async () => {
    setLoading(true);
    try {
      const response = await apiRequest(`/api/duplicates/phone/${phone}`, {
        method: 'GET'
      });

      if (response.success) {
        setPrimary(response.data.primary);
        setDuplicates(response.data.duplicates);
        setPrimaryRemarks(response.data.primaryRemarks || []);

        // Auto-select all duplicates by default
        const allUuids = new Set<string>(response.data.duplicates.map((d: Lead) => d.uuid));
        setSelectedDuplicates(allUuids);
      } else {
        showToast(response.message || 'Failed to fetch leads', 'error');
      }
    } catch (error: any) {
      showToast(error.message || 'Failed to fetch leads', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleDuplicateSelection = (uuid: string) => {
    setSelectedDuplicates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(uuid)) {
        newSet.delete(uuid);
      } else {
        newSet.add(uuid);
      }
      return newSet;
    });
  };

  const handleBulkReview = async () => {
    if (!reviewRemark.trim()) {
      showToast('Please add a remark before reviewing', 'error');
      return;
    }

    if (selectedDuplicates.size === 0) {
      showToast('Please select at least one duplicate to review', 'error');
      return;
    }

    try {
      const uuidsArray = Array.from(selectedDuplicates);

      // Use bulk review API endpoint
      const response = await apiRequest(`/api/duplicates/bulk/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uuids: uuidsArray,
          remark: reviewRemark
        })
      });

      if (response.success) {
        showToast(`Successfully reviewed ${response.data.reviewed_count} duplicate(s)`, 'success');
        setReviewRemark('');
        setSelectedDuplicates(new Set());

        // Remove reviewed duplicates from list
        setDuplicates(duplicates.filter(d => !selectedDuplicates.has(d.uuid)));

        if (duplicates.length <= selectedDuplicates.size) {
          // No more duplicates, close slider
          onReviewComplete?.();
          onClose();
        }
      } else {
        showToast(response.message || 'Failed to review duplicates', 'error');
      }
    } catch (error: any) {
      showToast(error.message || 'Failed to review duplicates', 'error');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
        onClick={onClose}
      />

      {/* Slider */}
      <div className="fixed right-0 top-0 h-full w-full max-w-4xl bg-white shadow-2xl z-[70] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Duplicate Review</h2>
            <p className="text-sm text-gray-600 mt-1">
              📞 {phone} ({duplicates.length} duplicate submission{duplicates.length !== 1 ? 's' : ''})
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
              <p className="text-gray-600 mt-4">Loading leads...</p>
            </div>
          ) : (
            <>
              {/* Primary Lead */}
              {primary && (
                <div className="bg-brandColorBg border-2 border-brandColor rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="bg-brandColor text-white px-3 py-1 rounded-full text-sm font-semibold">
                        ⭐ PRIMARY LEAD
                      </div>
                      <span className="text-sm text-gray-600">
                        Lead ID: #{primary.lead_id}
                      </span>
                    </div>
                    {onEditLead && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEditLead(primary as LeadType)}
                        leftIcon={<Edit className="h-4 w-4" />}
                        className="whitespace-nowrap hover:border-gray-300"
                      >
                        Edit Lead
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500">Name</label>
                      <p className="text-sm text-gray-900 mt-1">{primary.name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Email</label>
                      <p className="text-sm text-gray-900 mt-1">{primary.email || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Phone</label>
                      <p className="text-sm text-gray-900 mt-1">{primary.phone}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Created</label>
                      <p className="text-sm text-gray-900 mt-1">
                        {format(new Date(primary.created_date), 'MMM dd, yyyy hh:mm a')}
                      </p>
                    </div>
                  </div>

                  {primary.message && (
                    <div className="mt-4">
                      <label className="text-xs font-medium text-gray-500">Message</label>
                      <p className="text-sm text-gray-900 mt-1">{primary.message}</p>
                    </div>
                  )}

                  {primaryRemarks.length > 0 && (
                    <div className="mt-4">
                      <label className="text-xs font-medium text-gray-500">
                        Remarks ({primaryRemarks.length})
                      </label>
                      <div className="mt-2 space-y-2 max-h-32 overflow-y-auto">
                        {primaryRemarks.slice(0, 3).map((remark) => (
                          <div key={remark.uuid} className="text-xs bg-white p-2 rounded border">
                            <p className="text-gray-700">{remark.remark}</p>
                            <p className="text-gray-500 mt-1">
                              {remark.user?.userName} • {format(new Date(remark.date_remark), 'MMM dd, yyyy')}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Duplicates */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  📋 Duplicate Submissions (Sorted by Latest)
                </h3>

                {duplicates.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-3" />
                    <p>All duplicates have been reviewed!</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-3">
                      {duplicates.map((duplicate, index) => (
                      <div key={duplicate.uuid} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <input
                            type="checkbox"
                            checked={selectedDuplicates.has(duplicate.uuid)}
                            onChange={() => toggleDuplicateSelection(duplicate.uuid)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex items-center gap-2 flex-1">
                            <span className="text-sm font-medium text-gray-700">
                              Duplicate #{index + 1}
                            </span>
                            <span className="text-xs text-gray-500">
                              {format(new Date(duplicate.created_date), 'MMM dd, yyyy hh:mm a')}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="text-xs font-medium text-gray-500">Name</label>
                            <p className="text-sm text-gray-900 mt-1">{duplicate.name || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500">Email</label>
                            <p className="text-sm text-gray-900 mt-1">{duplicate.email || 'N/A'}</p>
                          </div>
                        </div>

                        {duplicate.message && (
                          <div className="mb-3">
                            <label className="text-xs font-medium text-gray-500">Message</label>
                            <p className="text-sm text-gray-900 mt-0.5 line-clamp-2">{duplicate.message}</p>
                          </div>
                        )}

                        {/* UTM Data */}
                        {(duplicate.utm_source || duplicate.utm_medium || duplicate.utm_campaign) && (
                          <div className="p-2 bg-gray-50 rounded">
                            <label className="text-xs font-medium text-gray-500">UTM</label>
                            <div className="grid grid-cols-3 gap-2 mt-1">
                              {duplicate.utm_source && (
                                <div className="text-xs">
                                  <span className="text-gray-500">Src:</span>{' '}
                                  <span className="text-gray-900">{duplicate.utm_source}</span>
                                </div>
                              )}
                              {duplicate.utm_medium && (
                                <div className="text-xs">
                                  <span className="text-gray-500">Med:</span>{' '}
                                  <span className="text-gray-900">{duplicate.utm_medium}</span>
                                </div>
                              )}
                              {duplicate.utm_campaign && (
                                <div className="text-xs">
                                  <span className="text-gray-500">Camp:</span>{' '}
                                  <span className="text-gray-900">{duplicate.utm_campaign}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                      </div>
                    ))}
                    </div>

                    {/* Remark Input - Moved to bottom */}
                    <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        Add Review Remark (applies to all selected duplicates)
                      </label>
                      <textarea
                        value={reviewRemark}
                        onChange={(e) => setReviewRemark(e.target.value)}
                        placeholder="Add a remark about the duplicates you're reviewing..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows={3}
                      />
                    </div>

                    {/* Single Review Button */}
                    <div className="mt-4 flex justify-end">
                      <Button
                        onClick={handleBulkReview}
                        disabled={!reviewRemark.trim() || selectedDuplicates.size === 0}
                        leftIcon={<CheckCircle className="h-4 w-4" />}
                        className="whitespace-nowrap hover:border-gray-300"
                      >
                        Review & Delete Selected ({selectedDuplicates.size})
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default DuplicateReviewSlider;
